<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import orderIconDown from '@/assets/icons/green/order_down.svg'
import orderIconUp from '@/assets/icons/green/order_up.svg'
import orderIconGrey from '@/assets/icons/grey/order.svg'
import arrowIcon from '@/assets/icons/grey/arrow_down.svg'

const props = defineProps<{
  modelValue: string | null
  order: string | null
  orderingOptions: { value: string; label: string }[]
  isSecondary?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
  (e: 'update-order', value: string): void
}>()

const localValue = ref<string | null>(null)

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      localValue.value = null
      return
    }

    const match = props.orderingOptions.find(
      (opt) => opt.label === val || opt.value === val
    )

    localValue.value = match ? match.label : val
  },
  { immediate: true }
)

watch(localValue, (newValue) => {
  emit('update:modelValue', newValue)
})

const toggleOrder = () => {
  emit('update-order', props.order === 'asc' ? 'desc' : 'asc')
}

const orderIcon = computed(() => {
  if (props.order === 'asc') return orderIconDown
  if (props.order === 'desc') return orderIconUp
  return orderIconGrey
})
</script>

<template>
  <div class="mb-4">
    <template v-if="isSecondary">
      <v-divider class="my-5" />
      <h3 class="text-subtitle-1 font-weight-bold pb-3">
        {{ $t('corporate.order.modalSecondLabel') }}
      </h3>
    </template>

    <!-- troca do grid por flex responsivo -->
    <div
      class="order-row d-flex flex-column flex-sm-row align-stretch align-sm-center"
    >
      <div class="order-select flex-grow-1">
        <v-select
          v-model="localValue"
          :items="orderingOptions"
          item-title="label"
          item-value="label"
          :placeholder="$t('corporate.order.title')"
          density="comfortable"
          variant="outlined"
          clearable
          hide-details
          class="w-100"
        >
          <template #append-inner>
            <v-img :src="arrowIcon" width="20" height="20" class="me-2" />
          </template>
        </v-select>
      </div>

      <div class="order-icon d-flex justify-sm-center align-center justify-end">
        <v-img
          id="order-button"
          :src="orderIcon"
          width="32"
          height="32"
          class="rounded-circle border-0"
          @click="toggleOrder"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* desktop mantém como era (select + ícone ao lado) */
.order-row {
  gap: 12px;
}

/* evita o select "estourar" e força ele a ceder espaço pro ícone */
.order-select {
  min-width: 0;
}

/* no mobile: uma linha (select + ícone do lado) e com altura/área clicável boa */
@media (max-width: 600px) {
  .order-row {
    flex-direction: row !important;
    align-items: center !important;
    gap: 10px;
  }

  .order-icon {
    flex: 0 0 auto;
  }

  /* reduz um pouco o padding/altura do select pra caber melhor */
  :deep(.v-field) {
    min-height: 44px;
  }
  :deep(.v-field__input) {
    padding-top: 8px;
    padding-bottom: 8px;
  }
}
</style>
